// components/chat/ConversationList.tsx
import { FbConversation } from '@/types/fb-type';
import { Loader } from 'lucide-react';
import * as React from 'react';
import { ConversationRow } from './ConversationRow';

type ConversationListProps = {
  items: FbConversation[];
  activeId?: string | null;
  isLoading?: boolean;
  onSelect: (conv: FbConversation) => void;
  /** Called when end is reached (infinite scroll) */
  onEndReached?: () => void;
  /** Pixels from bottom to trigger onEndReached */
  endReachedOffset?: number;
  /** Optional render override for each row */
  renderItem?: (args: {
    item: FbConversation;
    active: boolean;
    onClick: () => void;
  }) => React.ReactNode;
  emptyLabel?: string;
};

export function ConversationList({
  items,
  activeId = null,
  isLoading = false,
  onSelect,
  onEndReached,
  endReachedOffset = 120,
  renderItem,
  emptyLabel = 'No conversations yet',
}: ConversationListProps) {
  const listRef = React.useRef<HTMLDivElement>(null);

  // Infinite scroll
  React.useEffect(() => {
    if (!onEndReached) return;
    const el = listRef.current;
    if (!el) return;

    const onScroll = () => {
      const { scrollTop, clientHeight, scrollHeight } = el;
      const fromBottom = scrollHeight - (scrollTop + clientHeight);
      if (fromBottom <= endReachedOffset) onEndReached();
    };
    el.addEventListener('scroll', onScroll);
    return () => el.removeEventListener('scroll', onScroll);
  }, [onEndReached, endReachedOffset]);

  // Keyboard navigation (↑/↓ + Enter)
  const [focusIndex, setFocusIndex] = React.useState<number>(-1);
  React.useEffect(() => setFocusIndex(-1), [items.length]);

  const onKeyDown = (e: React.KeyboardEvent) => {
    if (!items.length) return;
    if (e.key === 'ArrowDown') {
      e.preventDefault();
      setFocusIndex((i) => Math.min(i + 1, items.length - 1));
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      setFocusIndex((i) => Math.max(i - 1, 0));
    } else if (e.key === 'Enter' && focusIndex >= 0) {
      e.preventDefault();
      onSelect(items[focusIndex]);
    }
  };

  return (
    <div
      ref={listRef}
      tabIndex={0}
      onKeyDown={onKeyDown}
      role="listbox"
      aria-label="Conversations"
      className="h-full min-h-0 w-full max-w-full overflow-y-auto overflow-x-hidden"
    >
      {/* Loading skeleton */}
      {isLoading && !items.length ? (
        <ListSkeleton />
      ) : items.length === 0 ? (
        <div className="p-6 text-center text-sm text-slate-500">{emptyLabel}</div>
      ) : (
        <ul className="divide-y divide-slate-100">
          {items.map((item, idx) => {
            const active = activeId === item.id;
            const onClick = () => onSelect(item);
            return (
              <li key={item.id} role="option" aria-selected={active}>
                {renderItem ? (
                  renderItem({ item, active, onClick })
                ) : (
                  <ConversationRow
                    item={item}
                    active={active}
                    focused={idx === focusIndex}
                    onClick={onClick}
                  />
                )}
              </li>
            );
          })}
        </ul>
      )}

      {/* Tail skeleton for "loading more" */}
      {isLoading && items.length > 0 && <Loader />}
    </div>
  );
}

/* Pretty loading skeleton */
function ListSkeleton() {
  return (
    <div className="animate-pulse divide-y divide-slate-100">
      {Array.from({ length: 6 }).map((_, i) => (
        <div key={i} className="flex items-center gap-3 px-3 py-2">
          <div className="h-10 w-10 rounded-full bg-slate-200" />
          <div className="min-w-0 flex-1 space-y-1.5">
            <div className="h-4 w-1/2 rounded bg-slate-200" />
            <div className="h-3 w-3/4 rounded bg-slate-200" />
            <div className="h-2.5 w-1/3 rounded bg-slate-200" />
          </div>
        </div>
      ))}
    </div>
  );
}
